import { useCallback, useEffect, useMemo, useState } from 'react';
import { useClinicSwitch } from './useClinicSwitch';
import { ALL_CLINICS, readActiveClinicId, writeActiveClinicId } from '@parsina/framework/clinical/src/lib/activeClinic';
import { IRAN_OFFSET_MS } from '@parsina/framework/clinical/src/lib/jalali';

export { ALL_CLINICS };

/**
 * «خودکار — طبق برنامهٔ حضور»: follow the clinic the doctor's presence schedule
 * names for the day on screen. Never STORED as this string in the kit's key —
 * the kit's other reader of it (the file uploader) treats anything but '' / ALL
 * as a clinic id. AUTO is stored as an EMPTY id WITH a pick day: «chose to
 * follow the schedule today», as opposed to an empty id with no day, which is
 * «never chose» and means every clinic.
 */
export const AUTO_CLINIC = 'AUTO';

// The Iran-local day a manual pick was made on. A pick lasts until the end of
// that day, then the lens shows every clinic again: otherwise one tap would hide
// the other clinics' appointments for good.
const PICK_DAY_KEY = 'parsina_active_clinic_day_v1';

const iranTodayISO = () => new Date(Date.now() + IRAN_OFFSET_MS).toISOString().slice(0, 10);

function readPickDay() {
  try { return localStorage.getItem(PICK_DAY_KEY) || ''; } catch { return ''; }
}

function writePickDay(day) {
  try {
    if (day) localStorage.setItem(PICK_DAY_KEY, day);
    else localStorage.removeItem(PICK_DAY_KEY);
  } catch {
    // Storage blocked — the pick simply lasts this session.
  }
}

// What is stored, read once:
//   '' and no day   → never chose today: every clinic (the default);
//   '' and a day    → AUTO, picked on that day;
//   anything else   → a manual pick (a clinic, or a legacy ALL) with its day.
// A pick from before picks expired has no day — it is given today (one day of
// grace), not dropped mid-shift.
function readStored() {
  const id = readActiveClinicId();
  if (!id) return { id: '', day: readPickDay() };
  let day = readPickDay();
  if (!day) { day = iranTodayISO(); writePickDay(day); }
  return { id, day };
}

/**
 * «پذیرش در کدام مطب؟» — the clinic whose day sheet پذیرش is showing.
 *
 * The doctor panel is deliberately clinic-agnostic everywhere else: a doctor's
 * record of a patient is the same record whichever desk booked the visit. The
 * DAY is the exception. A doctor at two clinics saw both clinics' patients in
 * one list with nothing to tell them apart, so «who is next» was answerable
 * only by memory of which building they were standing in.
 *
 * Three states, and the difference matters:
 *
 *   • one clinic   → there is nothing to choose. The chip states the fact and
 *                    does not open; filtering by the only clinic is a no-op.
 *   • many clinics → by default the lens shows EVERY clinic, each row tagged
 *                    with its clinic. A doctor at two clinics must never open
 *                    پذیرش to find one clinic's patients missing. A pick — one
 *                    clinic, or «خودکار» (follow the presence schedule: the
 *                    clinic `scheduledClinicId` names for the day on screen, or
 *                    every clinic when it names none) — lasts until the end of
 *                    the Iran day it was made on.
 *   • no clinic    → there is no chip at all (§2.1: a control is absent, never
 *                    disabled) and پذیرش shows the connection guide instead.
 *
 * **Never send `x-active-clinic-id` from here.** The BFF reads that header into
 * `clinicId` for EVERY resolver — orders, accounting, invoices — so a view
 * filter on one screen would silently re-scope writes on others (clinicalencounter
 * refuses cross-clinic order writes). This is a lens on data the doctor is
 * already entitled to see, and it stays one: the filtering happens here, on
 * `appointment.clinicId`, which the BFF already returns.
 */
/**
 * @param {object}   [opts]
 * @param {function} [opts.scheduledFor] (clinics) → the clinic id the presence
 *   schedule names for the day on screen, or null. A function because the
 *   answer depends on the membership this hook owns (Reception.shared
 *   `resolvePlace` drops windows at clinics the doctor has left).
 * @param {string}   [opts.todayISO] the screen's Iran-local today, so a pick
 *   expires on the same tick the screen rolls its date over.
 */
export function useActiveClinic({ scheduledFor, todayISO } = {}) {
  const { targets, pending, loading, error, refresh } = useClinicSwitch();
  const scheduledClinicId = useMemo(
    () => (scheduledFor && !loading ? scheduledFor(targets) : null),
    [scheduledFor, loading, targets],
  );
  const [stored, setStored] = useState(readStored);
  const today = todayISO || iranTodayISO();

  // «همهٔ مطب‌ها» is the default, so choosing it CLEARS the pick rather than
  // storing one: there is nothing to expire.
  const setActive = useCallback((id) => {
    const all = !id || id === ALL_CLINICS;
    const next = all || id === AUTO_CLINIC ? '' : id;
    const day = all ? '' : (todayISO || iranTodayISO());
    setStored({ id: next, day });
    writeActiveClinicId(next);
    writePickDay(day);
  }, [todayISO]);

  // A pick made on an earlier day has expired: clear it, so a reload tomorrow
  // does not resurrect it either.
  const expired = !!stored.day && stored.day !== today;
  useEffect(() => {
    if (!expired) return;
    writeActiveClinicId('');
    writePickDay('');
    setStored({ id: '', day: '' });
  }, [expired]);

  const mode = expired || !stored.day || stored.id === ALL_CLINICS ? 'all' : !stored.id ? 'auto' : 'pick';

  // Every id is resolved against the membership NOW, every render: a doctor
  // removed from a clinic would otherwise keep filtering پذیرش by a clinic
  // they are no longer in — an empty day sheet with no visible reason. AUTO
  // also answers «all» while the schedule is unknown, so the first paint shows
  // everything and then narrows, never the reverse (rows appearing reads as
  // new bookings).
  //
  // With ONE membership the chip names that clinic, but `filterId` stays ALL:
  // filtering by the only clinic is meant to be a no-op, and when it is not —
  // rows from a clinic the doctor has left, or a membership the lookup missed —
  // it would hide them behind a «نمایش همه» that has nothing to widen to.
  const activeId = useMemo(() => {
    if (loading) return ALL_CLINICS;
    if (targets.length === 1) return targets[0].id;          // no choice to make
    const member = (id) => targets.some((c) => c.id === id);
    if (mode === 'all') return ALL_CLINICS;
    if (mode === 'pick' && member(stored.id)) return stored.id;
    return scheduledClinicId && member(scheduledClinicId) ? scheduledClinicId : ALL_CLINICS;
  }, [loading, targets, mode, stored.id, scheduledClinicId]);

  const isAll = activeId === ALL_CLINICS;
  const filterId = targets.length > 1 ? activeId : ALL_CLINICS;
  const activeClinic = useMemo(
    () => (isAll ? null : targets.find((c) => c.id === activeId) || null),
    [isAll, targets, activeId],
  );

  return {
    clinics: targets,
    pending,
    loading,
    activeId,
    /** What to pass to `scopeToClinic` — never narrower than the chip can widen. */
    filterId,
    activeClinic,
    isAll,
    /** 'auto' (follows the schedule) | 'all' | 'pick' — only meaningful with >1 clinic. */
    mode: targets.length > 1 ? mode : 'pick',
    setActive,
    refresh,
    /** Exactly one membership: the chip informs, it does not offer a choice. */
    single: !loading && targets.length === 1,
    /** The membership lookup FAILED — we do not know whether there is a clinic. */
    error,
    /** No membership at all, as the server said — پذیرش renders the connection
     *  guide. Never true after a failed lookup: that is `error`. */
    none: !loading && !error && targets.length === 0,
  };
}

/**
 * The day's rows for the active clinic, plus what that choice is hiding.
 *
 * `hidden` is not bookkeeping: a filter that silently drops appointments is a
 * clinical hazard, so پذیرش says how many rows the current lens is holding back
 * and offers one tap to see them. Rows with no `clinicId` (pre-migration, or a
 * clinic the membership query does not know) are ALWAYS shown — dropping a real
 * appointment because its provenance is unclear is the one outcome worth
 * ruling out.
 */
export function scopeToClinic(appointments, activeId) {
  const list = appointments || [];
  if (!activeId || activeId === ALL_CLINICS) return { rows: list, hidden: 0 };
  const rows = list.filter((a) => !a.clinicId || a.clinicId === activeId);
  return { rows, hidden: list.length - rows.length };
}
